import { coerceMoneyFromApi } from '@shared/utils/money';
import {
  PosInformeCancelledOrder,
  PosInformeCategoryRow,
  PosInformeDashboard,
  PosInformePaymentMethod,
  PosInformeProductRow,
} from '../../models/pos-informe.models';
import { resolvePosDisplayOrderNumber } from '../../utils/pos-display-order-number.utils';

function pickString(raw: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = raw[key];
    if (value != null && String(value).trim() !== '') {
      return String(value);
    }
  }
  return null;
}

function pickNumber(raw: Record<string, unknown>, ...keys: string[]): number {
  for (const key of keys) {
    if (raw[key] != null && raw[key] !== '') {
      return Number(raw[key]) || 0;
    }
  }
  return 0;
}

function pickMoney(raw: Record<string, unknown>, ...keys: string[]): number {
  for (const key of keys) {
    if (raw[key] != null && raw[key] !== '') {
      return coerceMoneyFromApi(raw[key]) ?? 0;
    }
  }
  return 0;
}

export function mapPosInformePaymentMethod(raw: Record<string, unknown>): PosInformePaymentMethod {
  return {
    name: pickString(raw, 'nombre', 'name', 'label', 'method') || '—',
    personalizado: pickMoney(raw, 'personalizado'),
    ventas: pickMoney(raw, 'ventas', 'sales'),
    reembolso: pickMoney(raw, 'reembolso', 'refund'),
    total: pickMoney(raw, 'total'),
  };
}

export function mapPosInformeCancelledOrder(raw: Record<string, unknown>): PosInformeCancelledOrder {
  const paymentsRaw = Array.isArray(raw['payments'])
    ? (raw['payments'] as Array<Record<string, unknown>>)
    : Array.isArray(raw['pos_payments'])
      ? (raw['pos_payments'] as Array<Record<string, unknown>>)
      : [];

  return {
    id: String(raw['id'] ?? ''),
    orderNumber: resolvePosDisplayOrderNumber({
      number:
        raw['number'] != null && raw['number'] !== '' ? Number(raw['number']) || null : null,
      orderNumber: pickString(raw, 'order_number', 'orderNumber'),
    }),
    completedAt: pickString(raw, 'completed_at', 'completedAt'),
    total: pickMoney(raw, 'total'),
    cancellationNote: pickString(raw, 'cancellation_note', 'cancellationNote'),
    userName: pickString(raw, 'user_name', 'userName'),
    payments: paymentsRaw.map((p) => ({
      method: String(p['method'] ?? p['payment_method'] ?? p['paymentMethod'] ?? ''),
      amount: coerceMoneyFromApi(p['amount']) ?? 0,
      paymentType: String(p['payment_type'] ?? p['paymentType'] ?? 'reembolso'),
    })),
  };
}

export function mapPosInformeCategories(
  raw: unknown,
  totalGeneral: number
): PosInformeCategoryRow[] {
  const entries: Array<{ name: string; total: number }> = [];

  if (Array.isArray(raw)) {
    for (const entry of raw) {
      const record = entry as Record<string, unknown>;
      entries.push({
        name: pickString(record, 'nombre', 'name', 'label', 'categoria', 'category') || '—',
        total: pickMoney(record, 'total', 'amount', 'value'),
      });
    }
  } else if (raw && typeof raw === 'object') {
    for (const [name, value] of Object.entries(raw as Record<string, unknown>)) {
      entries.push({ name, total: coerceMoneyFromApi(value) ?? 0 });
    }
  }

  const base = totalGeneral > 0 ? totalGeneral : entries.reduce((sum, row) => sum + row.total, 0);
  return entries
    .map((row) => ({
      name: row.name,
      total: row.total,
      percent: base > 0 ? Math.round((row.total / base) * 1000) / 10 : 0,
    }))
    .sort((a, b) => b.total - a.total);
}

export function mapPosInformeProducts(
  raw: unknown,
  totalGeneral: number
): PosInformeProductRow[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  const entries = raw.map((entry) => {
    const record = entry as Record<string, unknown>;
    return {
      name: pickString(record, 'name', 'nombre', 'product_name', 'productName') || '—',
      quantity: pickNumber(record, 'quantity', 'qty', 'cantidad'),
      total: pickMoney(record, 'total', 'amount', 'revenue'),
    };
  });

  const base = totalGeneral > 0 ? totalGeneral : entries.reduce((sum, row) => sum + row.total, 0);
  return entries
    .map((row) => ({
      name: row.name,
      quantity: row.quantity,
      total: row.total,
      percent: base > 0 ? Math.round((row.total / base) * 1000) / 10 : 0,
    }))
    .sort((a, b) => b.total - a.total);
}

export function mapPosInformeDashboard(data: Record<string, unknown>): PosInformeDashboard {
  const methodsRaw = Array.isArray(data['metodosPago'] ?? data['metodos_pago'])
    ? ((data['metodosPago'] ?? data['metodos_pago']) as Array<Record<string, unknown>>)
    : [];
  const paymentMethods = methodsRaw.map(mapPosInformePaymentMethod);

  const totalVentas = pickMoney(data, 'totalVentas', 'total_ventas');
  const totalPersonalizado =
    pickMoney(data, 'totalPersonalizado', 'total_personalizado') ||
    paymentMethods.reduce((sum, row) => sum + row.personalizado, 0);
  const totalReembolso = pickMoney(data, 'totalReembolso', 'total_reembolso');
  const totalGeneral = pickMoney(data, 'totalGeneral', 'total_general');
  const ordenesCompletadas = pickNumber(data, 'ordenesCompletadas', 'ordenes_completadas');

  const cancelledRaw = Array.isArray(data['ordenesAnuladasDetalle'] ?? data['ordenes_anuladas_detalle'])
    ? ((data['ordenesAnuladasDetalle'] ?? data['ordenes_anuladas_detalle']) as Array<
        Record<string, unknown>
      >)
    : [];

  return {
    locationId: String(data['locationId'] ?? data['location_id'] ?? ''),
    reportDate: pickString(data, 'reportDate', 'report_date'),
    startDatetime: pickString(data, 'startDatetime', 'start_datetime'),
    endDatetime: pickString(data, 'endDatetime', 'end_datetime'),
    totalVentas,
    totalPersonalizado,
    totalReembolso,
    totalGeneral,
    totalPropinas: pickMoney(data, 'totalPropinas', 'total_propinas'),
    totalDescuentoCarrito: pickMoney(data, 'totalDescuentoCarrito', 'total_descuento_carrito'),
    totalDescuentoArticulo: pickMoney(data, 'totalDescuentoArticulo', 'total_descuento_articulo'),
    totalEnvios: pickMoney(data, 'totalEnvios', 'total_envios'),
    ordenesCompletadas,
    ordenesAnuladas: pickNumber(data, 'ordenesAnuladas', 'ordenes_anuladas'),
    hasData: Boolean(data['hasData'] ?? data['has_data']),
    paymentMethods,
    categories: mapPosInformeCategories(
      data['ventasPorCategoria'] ?? data['ventas_por_categoria'],
      totalGeneral
    ),
    products: mapPosInformeProducts(
      data['ventasPorProducto'] ?? data['ventas_por_producto'],
      totalGeneral
    ),
    cancelledOrders: cancelledRaw.map(mapPosInformeCancelledOrder).filter((row) => !!row.id),
    ticketPromedio: ordenesCompletadas > 0 ? totalGeneral / ordenesCompletadas : 0,
  };
}
